import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { STRIPE_CONFIG } from '../config/stripe';
import { getSubscriptionDetails } from '../services/stripe';
import { getDoc, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { secureLog } from '../utils/securityUtils';

interface SubscriptionContextType {
  subscriptionStatus: string;
  subscriptionTier: string;
  isSubscriptionActive: boolean;
  subscriptionEndDate: Date | null;
  loading: boolean;
  error: string | null;
  isInitialized: boolean;
  isPremium: boolean;
  isLifetime: boolean;
  refreshSubscription: () => Promise<void>;
}

// Clés pour le localStorage
const STORAGE_KEYS = {
  SUBSCRIPTION_STATUS: 'subscription_status',
  SUBSCRIPTION_TIER: 'subscription_tier',
  IS_SUBSCRIPTION_ACTIVE: 'is_subscription_active',
  SUBSCRIPTION_END_DATE: 'subscription_end_date',
  IS_PREMIUM: 'subscription_is_premium',
  IS_LIFETIME: 'subscription_is_lifetime',
  LAST_UPDATED: 'subscription_last_updated'
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('inactive');
  const [subscriptionTier, setSubscriptionTier] = useState<string>('CHECKING'); // État initial qui indique qu'on vérifie
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<boolean>(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [isLifetime, setIsLifetime] = useState<boolean>(false);
  
  const { user } = useAuth();

  // Fonction pour charger depuis le localStorage
  const loadFromCache = (userId: string) => {
    try {
      const cachedStatus = localStorage.getItem(`${STORAGE_KEYS.SUBSCRIPTION_STATUS}_${userId}`);
      const cachedTier = localStorage.getItem(`${STORAGE_KEYS.SUBSCRIPTION_TIER}_${userId}`);
      const cachedActive = localStorage.getItem(`${STORAGE_KEYS.IS_SUBSCRIPTION_ACTIVE}_${userId}`);
      const cachedEndDate = localStorage.getItem(`${STORAGE_KEYS.SUBSCRIPTION_END_DATE}_${userId}`);
      const cachedIsPremium = localStorage.getItem(`${STORAGE_KEYS.IS_PREMIUM}_${userId}`);
      const cachedIsLifetime = localStorage.getItem(`${STORAGE_KEYS.IS_LIFETIME}_${userId}`);
      const lastUpdated = localStorage.getItem(`${STORAGE_KEYS.LAST_UPDATED}_${userId}`);

      // Vérifier si le cache n'est pas trop vieux (exemple: 1 heure)
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      const isExpired = !lastUpdated || parseInt(lastUpdated) < oneHourAgo;

      if (cachedStatus && cachedTier && !isExpired) {
        setSubscriptionStatus(cachedStatus);
        setSubscriptionTier(cachedTier);
        setIsSubscriptionActive(cachedActive === 'true');
        setSubscriptionEndDate(cachedEndDate ? new Date(cachedEndDate) : null);
        // Restaurer aussi isPremium / isLifetime depuis le cache (sinon ils restaient
        // à false pendant tout le chargement, ce qui barrait le menu d'un premium).
        setIsPremium(cachedIsPremium === 'true');
        setIsLifetime(cachedIsLifetime === 'true');
        setLoading(false);
        // NE PAS marquer comme initialisé avec le cache pour éviter le flash
        // On attend la vérification Firebase
        
        secureLog.log("SubscriptionContext - Loaded from cache:", {
          status: cachedStatus,
          tier: cachedTier,
          active: cachedActive === 'true',
          isPremium: cachedIsPremium === 'true',
          isLifetime: cachedIsLifetime === 'true'
        });
        
        return true; // Cache trouvé et utilisé
      }
    } catch (error) {
      secureLog.error('Error loading subscription from cache:', error);
    }
    return false; // Pas de cache valide
  };

  // Fonction pour sauvegarder dans le localStorage
  const saveToCache = (
    userId: string,
    status: string,
    tier: string,
    active: boolean,
    endDate: Date | null,
    premium: boolean,
    lifetime: boolean,
  ) => {
    try {
      localStorage.setItem(`${STORAGE_KEYS.SUBSCRIPTION_STATUS}_${userId}`, status);
      localStorage.setItem(`${STORAGE_KEYS.SUBSCRIPTION_TIER}_${userId}`, tier);
      localStorage.setItem(`${STORAGE_KEYS.IS_SUBSCRIPTION_ACTIVE}_${userId}`, active.toString());
      localStorage.setItem(`${STORAGE_KEYS.SUBSCRIPTION_END_DATE}_${userId}`, endDate ? endDate.toISOString() : '');
      localStorage.setItem(`${STORAGE_KEYS.IS_PREMIUM}_${userId}`, premium.toString());
      localStorage.setItem(`${STORAGE_KEYS.IS_LIFETIME}_${userId}`, lifetime.toString());
      localStorage.setItem(`${STORAGE_KEYS.LAST_UPDATED}_${userId}`, Date.now().toString());
      
      secureLog.log("SubscriptionContext - Saved to cache:", { status, tier, active, premium, lifetime });
    } catch (error) {
      secureLog.error('Error saving subscription to cache:', error);
    }
  };

  // Fonction pour nettoyer le cache
  const clearCache = (userId: string) => {
    try {
      localStorage.removeItem(`${STORAGE_KEYS.SUBSCRIPTION_STATUS}_${userId}`);
      localStorage.removeItem(`${STORAGE_KEYS.SUBSCRIPTION_TIER}_${userId}`);
      localStorage.removeItem(`${STORAGE_KEYS.IS_SUBSCRIPTION_ACTIVE}_${userId}`);
      localStorage.removeItem(`${STORAGE_KEYS.SUBSCRIPTION_END_DATE}_${userId}`);
      localStorage.removeItem(`${STORAGE_KEYS.IS_PREMIUM}_${userId}`);
      localStorage.removeItem(`${STORAGE_KEYS.IS_LIFETIME}_${userId}`);
      localStorage.removeItem(`${STORAGE_KEYS.LAST_UPDATED}_${userId}`);
    } catch (error) {
      secureLog.error('Error clearing subscription cache:', error);
    }
  };

  const refreshSubscription = async (useCache: boolean = false) => {
    if (!user) {
      setSubscriptionStatus('inactive');
      setSubscriptionTier(STRIPE_CONFIG.TIERS.FREE);
      setIsSubscriptionActive(false);
      setSubscriptionEndDate(null);
      setIsPremium(false);
      setIsLifetime(false);
      setIsInitialized(true);
      setLoading(false);
      return;
    }

    // Si on demande à utiliser le cache et qu'il est disponible, on s'arrête là
    if (useCache && loadFromCache(user.uid)) {
      // On lance quand même une vérification en arrière-plan
      setTimeout(() => refreshSubscription(false), 100);
      return;
    }

    try {
      setLoading(!useCache); // On ne montre le loading que si on n'utilise pas le cache
      setError(null);
      
      // Vérifier d'abord dans Firestore si l'utilisateur est premium
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      const nextIsPremium = userData?.isPremium === true || userData?.isPremium === 'true';
      const nextIsLifetime = userData?.isLifetime === true || userData?.isLifetime === 'true';
      // Un lifetime est TOUJOURS premium, même si le flag isPremium est manquant/incohérent.
      const effectivePremium = nextIsPremium || nextIsLifetime;
      
      secureLog.log("SubscriptionContext - User data from Firebase:", { nextIsPremium, nextIsLifetime, userData });

      let newStatus: string;
      let newTier: string;
      let newActive: boolean;
      let newEndDate: Date | null;

      if (effectivePremium) {
        newStatus = 'active';
        newTier = nextIsLifetime ? STRIPE_CONFIG.TIERS.LIFETIME : STRIPE_CONFIG.TIERS.PRO;
        newActive = true;
        
        // Si c'est un abonnement à vie, on ne définit pas de date de fin
        if (nextIsLifetime) {
          newEndDate = null;
        } else {
          newEndDate = userData?.premiumEndDate ? new Date(userData.premiumEndDate) : null;
        }
      } else {
        newStatus = 'inactive';
        newTier = STRIPE_CONFIG.TIERS.FREE;
        newActive = false;
        newEndDate = null;
      }

      // Mettre à jour les états AVANT de marquer comme initialisé
      setSubscriptionStatus(newStatus);
      setSubscriptionTier(newTier);
      setIsSubscriptionActive(newActive);
      setSubscriptionEndDate(newEndDate);
      setIsPremium(effectivePremium);
      setIsLifetime(nextIsLifetime);

      // Sauvegarder dans le cache
      saveToCache(user.uid, newStatus, newTier, newActive, newEndDate, effectivePremium, nextIsLifetime);

      // Marquer comme initialisé APRÈS avoir mis à jour tous les états
      setIsInitialized(true);

    } catch (error) {
      // IMPORTANT : une lecture Firestore qui échoue (token expiré après mise en
      // arrière-plan sur mobile, réseau coupé, etc.) ne doit JAMAIS rétrograder un
      // utilisateur premium en gratuit. On préserve l'état connu (cache) au lieu de
      // forcer un tier "free" qui barrerait tout le menu.
      secureLog.error('Error refreshing subscription:', error);
      setError('Failed to refresh subscription status');

      const cachedPremium = localStorage.getItem(`${STORAGE_KEYS.IS_PREMIUM}_${user.uid}`) === 'true';
      const cachedLifetime = localStorage.getItem(`${STORAGE_KEYS.IS_LIFETIME}_${user.uid}`) === 'true';
      const cachedTier = localStorage.getItem(`${STORAGE_KEYS.SUBSCRIPTION_TIER}_${user.uid}`);

      if (cachedPremium || cachedLifetime) {
        // On restaure l'état premium connu (le tier devient premium, donc marquer
        // initialisé est sûr et ne barre rien) et on retente en arrière-plan.
        setSubscriptionStatus('active');
        setSubscriptionTier(
          cachedLifetime
            ? STRIPE_CONFIG.TIERS.LIFETIME
            : (cachedTier && cachedTier !== STRIPE_CONFIG.TIERS.FREE ? cachedTier : STRIPE_CONFIG.TIERS.PRO),
        );
        setIsSubscriptionActive(true);
        setIsPremium(true);
        setIsLifetime(cachedLifetime);
        setIsInitialized(true);
        // Nouvelle tentative différée pour confirmer/corriger dès que le réseau revient.
        setTimeout(() => { void refreshSubscription(false); }, 5000);
      } else {
        // Aucun signal premium connu : on peut conclure (vraisemblablement gratuit).
        setIsInitialized(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      // Charger d'abord depuis le cache pour un affichage rapide
      const hasCachedData = loadFromCache(user.uid);
      
      // Puis rafraîchir depuis Firebase
      refreshSubscription(!hasCachedData);
    } else {
      // Nettoyer le cache si pas d'utilisateur
      setSubscriptionStatus('inactive');
      setSubscriptionTier(STRIPE_CONFIG.TIERS.FREE);
      setIsSubscriptionActive(false);
      setSubscriptionEndDate(null);
      setIsPremium(false);
      setIsLifetime(false);
      setIsInitialized(true);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    secureLog.log('Subscription Context State:', {
      status: subscriptionStatus,
      tier: subscriptionTier,
      isActive: isSubscriptionActive,
      endDate: subscriptionEndDate,
      loading,
      error,
      isInitialized
    });
  }, [subscriptionStatus, subscriptionTier, isSubscriptionActive, subscriptionEndDate, loading, error, isInitialized]);

  const value = {
    subscriptionStatus,
    subscriptionTier,
    isSubscriptionActive,
    subscriptionEndDate,
    loading,
    error,
    isInitialized,
    isPremium,
    isLifetime,
    refreshSubscription: () => refreshSubscription(false),
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
